/// <amd-module name="Test/TestPlansLibrary/TestPlansLibraryFilter/TestPlansLibraryFilterComponent" />
import * as React from "react";

import { IObservableValue } from "VSS/Core/Observable";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { IdentityPicker } from "VSSUI/IdentityPicker";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { FilterContext, IFilter } from "VSSUI/Utilities/Filter";

import {
    ITestPlansLibraryFilterComponentProps,
    ITestPlansLibraryFilterBarItemBase,
    ITestPlansLibraryFilterBarItem,
    ITestPlansLibraryFilterBarItemIdentity,
    ITestPlansLibraryFilterItem,
    TestPlansLibraryFilterItemType
} from "./TestPlansLibraryFilterContracts";

/**
 * `TestPlansLibraryFilterComponent`- This component actually renders the filter bar in UI.
 * This component is not supposed to depend on anything other than VSS UI components and its own contracts defined.
 */
export class TestPlansLibraryFilterComponent extends VssComponent<ITestPlansLibraryFilterComponentProps> {
    private pickerProvider: IPeoplePickerProvider;

    constructor(props: ITestPlansLibraryFilterComponentProps, context: IVssComponentContext) {
        super(props, context);

        this.pickerProvider = new PeoplePickerProvider(this.context.pageContext, {
            identityTypes: ["user", "group"]
        });
    }

    public render(): JSX.Element {
        return (
            <FilterContext.Consumer>
                {(value: { filter: IFilter; filterToggled: IObservableValue<boolean> }) => (
                    <Observer value={value.filterToggled}>
                        {() => {
                            return value.filterToggled.value ? (
                                <FilterBar className={this.props.className} filter={value.filter}>
                                    {this._renderFilterFields(value.filter)}
                                </FilterBar>
                            ) : null;
                        }}
                    </Observer>
                )}
            </FilterContext.Consumer>
        );
    }

    private _renderFilterFields(filter: IFilter): JSX.Element[] {
        const filterBarItemsToRender: JSX.Element[] = [];
        if (this.props.filterBarItems && this.props.filterBarItems.length > 0) {
            for (const filterBarItem of this.props.filterBarItems) {
                const filterItemToRender: JSX.Element = this._getFilterItemToRender(filterBarItem, filter);
                filterBarItemsToRender.push(filterItemToRender);
            }
        }

        return filterBarItemsToRender;
    }

    private _getFilterItemToRender(filterBarItemProps: ITestPlansLibraryFilterBarItemBase, filter: IFilter): JSX.Element {
        let filterBarItemToRender: JSX.Element = <></>;
        if (filterBarItemProps) {
            switch (filterBarItemProps.filterItemType) {
                case TestPlansLibraryFilterItemType.TextFilter:
                    filterBarItemToRender = (
                        <KeywordFilterBarItem
                            key={filterBarItemProps.key}
                            filter={filter}
                            filterItemKey={filterBarItemProps.filterItemKey}
                            placeholder={filterBarItemProps.placeholder}
                        />
                    );
                    break;

                case TestPlansLibraryFilterItemType.DropDownFilter:
                    filterBarItemToRender = (
                        <DropdownFilterBarItem
                            key={filterBarItemProps.key}
                            filter={filter}
                            filterItemKey={filterBarItemProps.filterItemKey}
                            placeholder={filterBarItemProps.placeholder}
                            selection={filterBarItemProps.selection}
                            items={[]}
                            width={filterBarItemProps.width}
                        />
                    );
                    break;

                case TestPlansLibraryFilterItemType.QueryDropdown:
                    filterBarItemToRender = (
                        <DropdownFilterBarItem
                            key={filterBarItemProps.key}
                            filter={filter}
                            filterItemKey={filterBarItemProps.filterItemKey}
                            placeholder={filterBarItemProps.placeholder}
                            selection={filterBarItemProps.selection}
                            items={this._getListBoxItemFromFilterItem((filterBarItemProps as ITestPlansLibraryFilterBarItem).items)}
                            width={filterBarItemProps.width}
                            hideClearAction={true}
                        />
                    );
                    break;

                case TestPlansLibraryFilterItemType.IdentityPicker:
                    const identityFilterBarItemProps: ITestPlansLibraryFilterBarItemIdentity = filterBarItemProps as ITestPlansLibraryFilterBarItemIdentity;

                    const identityAddedCallback = identityFilterBarItemProps.identityAdded ? identityFilterBarItemProps.identityAdded : () => {};

                    const identityRemovedCallback = identityFilterBarItemProps.identityRemoved
                        ? identityFilterBarItemProps.identityRemoved
                        : () => {};

                    const identitiesRemovedCallback = identityFilterBarItemProps.identitiesRemoved
                        ? identityFilterBarItemProps.identitiesRemoved
                        : () => {};

                    const selectedIdentities = identityFilterBarItemProps.identitySelection ? identityFilterBarItemProps.identitySelection : [];

                    filterBarItemToRender = (
                        <IdentityPicker
                            pickerProvider={this.pickerProvider}
                            onIdentityAdded={identity => identityAddedCallback(identity)}
                            onIdentityRemoved={identity => identityRemovedCallback(identity)}
                            onIdentitiesRemoved={identities => identitiesRemovedCallback(identities)}
                            selectedIdentities={selectedIdentities}
                            placeholderText={identityFilterBarItemProps.placeholder}
                        />
                    );
            }
        }

        return filterBarItemToRender;
    }

    private _getListBoxItemFromFilterItem(filterItems: ITestPlansLibraryFilterItem[] | undefined): IListBoxItem[] {
        let listBoxItems: IListBoxItem[] = [];

        if (filterItems && filterItems.length > 0) {
            filterItems.forEach((filterItem: ITestPlansLibraryFilterItem) => {
                listBoxItems.push({
                    id: filterItem.id,
                    text: filterItem.text
                });
            });
        }

        return listBoxItems;
    }
}

VssComponent.register("testplans-library-filter-component", TestPlansLibraryFilterComponent);
